import './BulkImportSection.css';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  importCandidatesFromCsv,
  readCandidatesCsvPreview,
  isCandidatePreviewRowInvalid,
} from '../../logic/candidatesLogic';
import { loadTestConfigs } from '../../logic/testConfigLogic';

export default function BulkImportSection({ notify, can }) {
  const [configs, setConfigs] = useState([]);
  const [csvTestConfigId, setCsvTestConfigId] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [csvResult, setCsvResult] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvPreviewLoading, setCsvPreviewLoading] = useState(false);
  const fileInputRef = useRef();

  const assignCsvFile = async (f) => {
    if (!f) return;
    setCsvFile(f);
    setCsvResult(null);
    setCsvPreview(null);
    setCsvPreviewLoading(true);
    try {
      const data = await readCandidatesCsvPreview(f);
      setCsvPreview(data);
    } catch (err) {
      notify(err.message || 'Could not read CSV preview', true);
      setCsvFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setCsvPreviewLoading(false);
    }
  };

  const clearCsvFile = () => {
    setCsvFile(null);
    setCsvResult(null);
    setCsvPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const loadConfigs = useCallback(async () => {
    try {
      const configs = await loadTestConfigs();
      setConfigs(configs);
    } catch (e) { notify(e.response?.data?.message || 'Failed to load configs', true); }
  }, [notify]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const uploadCsv = async () => {
    if (!csvFile) return;
    setCsvUploading(true); setCsvResult(null);
    try {
      const result = await importCandidatesFromCsv(csvFile, csvTestConfigId);
      setCsvResult(result);
      notify(`${result.created} created, ${result.skipped} skipped`);
      clearCsvFile();
    } catch (err) { notify(err.response?.data?.message || 'CSV upload failed', true); }
    finally { setCsvUploading(false); }
  };

  const downloadSample = () => {
    const csv = `full_name,email,phone,department,position\nJohn Doe,john@co.com,9876543210,Engineering,Developer\nJane Smith,jane@co.com,,HR,Recruiter`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download = 'sample_candidates.csv'; a.click();
  };

  return (
    <div className="create-form-wrap fade-up">
      {can('candidates','edit') ? (
        <>
          <div className="info-banner"><span>📧</span><div>
            <strong>Bulk import with auto-email</strong>
            <p>Upload a CSV. Unique usernames &amp; passwords are generated for each candidate and welcome emails are sent automatically.</p>
          </div></div>
          <div className="form-section" style={{marginBottom:'1rem'}}>
            <h3>Test Configuration</h3>
            <div className="input-group">
              <label>Assign Test Config to All Imported Candidates</label>
              <select className="input-field" value={csvTestConfigId} onChange={e=>setCsvTestConfigId(e.target.value)}>
                <option value="">— Use global active config —</option>
                {configs.map(c=>(
                  <option key={c.id} value={c.id}>
                    {c.title}{c.is_active?' (Active)':''} · {c.total_questions}Q · {c.duration_minutes}min
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="csv-format-card">
            <div className="csv-format-header">
              <span>📋 Required CSV Format</span>
              <button className="btn btn-outline btn-sm" onClick={downloadSample}>⬇ Sample CSV</button>
            </div>
            <div className="csv-columns">
              {[{col:'full_name',req:true,desc:'Full name'},{col:'email',req:true,desc:'Email address (unique)'},{col:'phone',req:false,desc:'Phone number'},{col:'department',req:false,desc:'Department'},{col:'position',req:false,desc:'Position applied for'}].map(c=>(
                <div key={c.col} className="csv-col-row">
                  <code>{c.col}</code>
                  <span className={`badge ${c.req?'badge-red':'badge-purple'}`}>{c.req?'Required':'Optional'}</span>
                  <span className="col-desc">{c.desc}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="csv-upload-area" onDragOver={e=>e.preventDefault()} onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f)assignCsvFile(f);}}>
            <input ref={fileInputRef} type="file" accept=".csv" style={{display:'none'}} onChange={e=>{const f=e.target.files[0];if(f)assignCsvFile(f);}} id="csv-input"/>
            <label htmlFor="csv-input" className="csv-drop-label">
              {csvPreviewLoading ? (
                <div className="bulk-preview-loading">
                  <span className="btn-spinner bulk-preview-spinner" />
                  <span>Reading file…</span>
                </div>
              ) : csvFile ? (
                <div className="csv-file-selected">
                  <span className="csv-file-icon">📄</span>
                  <div><strong>{csvFile.name}</strong><span>{(csvFile.size/1024).toFixed(1)} KB</span></div>
                  <button className="csv-remove-btn" type="button" onClick={e=>{e.preventDefault();clearCsvFile();}}>✕</button>
                </div>
              ) : (<><span className="csv-upload-icon">☁</span><strong>Drop CSV here or click to browse</strong><span>Max 5 MB · .csv only</span></>)}
            </label>
          </div>
          {csvPreview && !csvPreviewLoading && (
            <div className="bulk-csv-preview fade-up">
              <div className="bulk-csv-preview-header">
                <span className="bulk-csv-preview-count">{csvPreview.rows.length} row{csvPreview.rows.length !== 1 ? 's' : ''} ready to import</span>
                {csvPreview.rows.some(isCandidatePreviewRowInvalid) && (
                  <span className="bulk-csv-preview-warn">Some rows are missing full name or email — they may be skipped on import.</span>
                )}
              </div>
              <div className="bulk-csv-preview-scroll">
                <table className="bulk-csv-preview-table">
                  <thead>
                    <tr>
                      <th className="bulk-csv-preview-num">#</th>
                      {csvPreview.headers.map((h) => (
                        <th key={h}>{h.replace(/_/g, ' ')}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.rows.map((row, idx) => (
                      <tr key={idx} className={isCandidatePreviewRowInvalid(row) ? 'bulk-csv-row-invalid' : ''}>
                        <td className="bulk-csv-preview-num">{idx + 1}</td>
                        {csvPreview.headers.map((h) => (
                          <td key={h}>{row[h] ?? ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="form-actions">
            <button className="btn btn-primary" onClick={uploadCsv} disabled={!csvFile||csvUploading||csvPreviewLoading||!csvPreview}>
              {csvUploading?<><span className="btn-spinner"/>Importing…</>:'📂 Import Candidates'}
            </button>
          </div>
          {csvResult && (
            <div className="bulk-result fade-up">
              <h3>Import Summary</h3>
              <div className="bulk-stats">
                <div className="bulk-stat"><span className="bs-val">{csvResult.total}</span><span className="bs-lbl">Total Rows</span></div>
                <div className="bulk-stat success"><span className="bs-val">{csvResult.created}</span><span className="bs-lbl">Created</span></div>
                <div className="bulk-stat warn"><span className="bs-val">{csvResult.skipped}</span><span className="bs-lbl">Skipped</span></div>
              </div>
              {csvResult.skippedDetails?.length>0 && (
                <div className="skipped-list"><strong>Skipped:</strong>
                  {csvResult.skippedDetails.map((s,i)=><div key={i} className="skipped-row"><code>{s.email}</code> — {s.reason}</div>)}
                </div>
              )}
              <p className="bulk-email-note">📧 Welcome emails are being sent to {csvResult.created} candidate{csvResult.created!==1?'s':''} in the background.</p>
            </div>
          )}
        </>
      ) : (
        <div className="empty-state"><span>🔒</span><p>You do not have permission to bulk import candidates.</p></div>
      )}
    </div>
  );
}
